import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Bookmark, Layers, Lock } from 'lucide-react';

import {
  useCollectionsQuery,
  useCollectionEntitiesQuery,
  useRemoveEntityFromCollectionMutation,
  type AdminCollection,
} from '@/client/api/listing-management/collections.query';
import {
  getCollectionHex,
  getCollectionIconComponent,
} from '@/client/constants/collectionCustomization';
import { getAsset } from '@/client/utils/assets';
import { Skeleton } from '@/components/ui/skeleton';

const itemCountOf = (c: AdminCollection): number | null => {
  const anyc = c as any;
  const n = anyc.entity_count ?? anyc.item_count ?? anyc.items_count ?? anyc.entities_count;
  return typeof n === 'number' ? n : null;
};

/* ── Card, matching ff-ui's CollectionCard ── */
const CollectionCard = ({
  collection,
  onView,
}: {
  collection: AdminCollection;
  onView: () => void;
}) => {
  const hex = getCollectionHex(collection.color ?? undefined);
  const CardIcon = getCollectionIconComponent(collection.icon ?? undefined);
  const useCustomBg = Boolean(hex);
  const isPrivate = collection.private === 1 || collection.private === true;
  const count = itemCountOf(collection);

  const defaultPublicClass = 'bg-gradient-to-br from-sky-100/90 via-white to-emerald-50/70';
  const defaultPrivateClass = 'bg-gradient-to-br from-violet-100/85 via-fuchsia-50/50 to-rose-100/60';

  return (
    <div
      className="group relative cursor-pointer touch-manipulation rounded-2xl bg-white shadow-sm ring-1 ring-black/[0.06] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_45px_-12px_rgba(15,23,42,0.14)] hover:ring-black/[0.09]"
      onClick={onView}
    >
      <div
        className={`relative flex h-36 items-center justify-center overflow-hidden rounded-t-2xl sm:h-40 md:h-44 ${
          useCustomBg ? '' : isPrivate ? defaultPrivateClass : defaultPublicClass
        }`}
        style={useCustomBg && hex ? { backgroundColor: hex } : undefined}
      >
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/35 via-transparent to-black/[0.06]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-8 -top-12 h-40 w-40 rounded-full bg-white/25 blur-2xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-10 -left-6 h-32 w-32 rounded-full bg-black/[0.04] blur-2xl"
          aria-hidden
        />

        <div className="relative flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-2xl bg-white/55 shadow-inner ring-1 ring-white/70 backdrop-blur-sm transition-transform duration-300 ease-out group-hover:scale-105 group-hover:bg-white/65 sm:h-[4.75rem] sm:w-[4.75rem]">
          <CardIcon
            className={`h-[2.1rem] w-[2.1rem] drop-shadow-sm sm:h-10 sm:w-10 ${
              useCustomBg
                ? 'text-gray-800/85'
                : isPrivate
                ? 'text-violet-700/85'
                : 'text-emerald-800/80'
            }`}
          />
        </div>

        {isPrivate && (
          <div className="absolute left-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-xl bg-white/85 shadow-sm ring-1 ring-white/90 backdrop-blur-sm">
            <Lock className="h-3.5 w-3.5 text-violet-700" />
          </div>
        )}
      </div>

      <div className="relative rounded-b-2xl border-t border-gray-100/90 bg-gradient-to-b from-white to-gray-50/70 px-3.5 py-3.5 sm:px-4 sm:py-4">
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 flex-1 text-sm font-semibold leading-snug tracking-tight text-gray-900 sm:text-base">
            {collection.name ?? 'Untitled collection'}
          </h3>
          {isPrivate && (
            <span className="mt-0.5 inline-flex shrink-0 items-center gap-0.5 rounded-full bg-violet-100/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
              Private
            </span>
          )}
        </div>

        {count != null && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100/95 px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200/80 sm:text-xs">
              <Bookmark className="h-3 w-3 text-gray-500 sm:h-3.5 sm:w-3.5" strokeWidth={2.25} />
              <span>
                {count} {count === 1 ? 'item' : 'items'}
              </span>
            </span>
          </div>
        )}

        {collection.description && (
          <p className="line-clamp-2 text-xs leading-relaxed text-gray-500 sm:text-[13px]">
            {collection.description}
          </p>
        )}
      </div>
    </div>
  );
};

/* ── Items inside one collection (admin can remove; add is intentionally omitted) ── */
const CollectionItems = ({
  collection,
  onBack,
}: {
  collection: AdminCollection;
  onBack: () => void;
}) => {
  const ecolid = collection.ecolid!;
  const { data, isLoading } = useCollectionEntitiesQuery(ecolid);
  const removeMut = useRemoveEntityFromCollectionMutation();

  const body: any = data?.data && typeof data.data === 'object' ? data.data : data;
  const items: any[] = Array.isArray(body?.items)
    ? body.items
    : Array.isArray(body?.entities)
    ? body.entities
    : Array.isArray(body)
    ? body
    : [];

  const remove = async (eid: number) => {
    try {
      await removeMut.mutateAsync({ ecolid, eid });
      toast.success(`Listing #${eid} removed`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to remove the listing.');
    }
  };

  const CardIcon = getCollectionIconComponent(collection.icon ?? undefined);

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="h-4 w-4" /> Back to collections
      </button>
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg text-white"
            style={{ background: getCollectionHex(collection.color ?? undefined) || '#4f46e5' }}
          >
            <CardIcon className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{collection.name}</h3>
            {collection.description && (
              <p className="text-sm text-gray-500">{collection.description}</p>
            )}
          </div>
        </div>

        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">No listings in this collection.</p>
        ) : (
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
            {items.map((e: any) => {
              const eid = e.Entity?.eid ?? e.eid;
              const ent = e.Entity ?? e;
              const name = ent.name_basic || ent.name_marketing || ent.name || `Listing #${eid}`;
              const cover = getAsset(e.Assets?.[0] ?? ent.Assets?.[0]);
              const price = e.Prices?.[0]?.price ?? ent.Prices?.[0]?.price;
              const ccode = e.Prices?.[0]?.ccode ?? ent.Prices?.[0]?.ccode ?? '';
              return (
                <div key={e.id ?? eid} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      {cover ? (
                        <img src={cover} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300">
                          <Layers className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-gray-800">{name}</p>
                      <p className="text-xs text-gray-400">
                        eid {eid}
                        {price != null ? ` · ${ccode} ${Number(price).toLocaleString()}` : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(eid)}
                    disabled={eid == null || removeMut.isPending}
                    className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                    title="Remove from collection"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const CollectionsTab = ({ uid }: { uid: number }) => {
  const { data, isLoading, isError } = useCollectionsQuery({ uid });
  const collections = data?.collections ?? [];
  const [viewing, setViewing] = useState<AdminCollection | null>(null);

  if (viewing) {
    return <CollectionItems collection={viewing} onBack={() => setViewing(null)} />;
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-64 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
        Could not load collections.
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
        This user has no collections.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
      {collections.map(c => (
        <CollectionCard key={c.ecolid} collection={c} onView={() => setViewing(c)} />
      ))}
    </div>
  );
};

export default CollectionsTab;
